import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient
} from '@tanstack/react-query';
import {
    fetchMeta,
    fetchGroups,
    fetchCases,
    fetchCase,
    fetchManufacturerStatuses,
    searchCases,
    assignWorkQueueCase,
    unassignWorkQueueCase,
    type SortBy,
    type SortDir,
    type WorkflowMeta,
    type WorkQueueCase,
    type FetchGroupsResponse,
    type FetchCasesResponse,
    type SearchCasesResponse,
    type PriorityCounts
} from '../services/workQueue';

export type { PriorityCounts };
import { invalidateWorkQueue } from '../components/work-queue/util';
import { invalidateSidebarCounts } from './useSidebarCounts';
import { ASSIGNEE_URL_PARAM } from '../components/work-queue/constants';

const ONE_MINUTE = 60 * 1000;
const AUTO_REFRESH_MS = 2 * ONE_MINUTE;

export function useMeta() {
    return useQuery<WorkflowMeta>({
        queryKey: ['workflows', 'meta'],
        queryFn: fetchMeta,
        staleTime: ONE_MINUTE
    });
}

interface UseGroupsParams {
    practice?: string[];
    assignee?: string[];
    team?: string[];
    tags?: string[];
    monitoring?: string[];
    manufacturerStatus?: string[];
    priority?: string[];
}

// Normalise empty values to null so the query key is stable across "filter
// just turned off" vs "filter never set" - avoids spurious refetches.
function normaliseList(xs?: string[]): string[] | null {
    return xs && xs.length > 0 ? xs : null;
}

export function useGroups(params: UseGroupsParams = {}) {
    const practice = normaliseList(params.practice);
    const assignee = normaliseList(params.assignee);
    const team = normaliseList(params.team);
    const tags = normaliseList(params.tags);
    const monitoring = normaliseList(params.monitoring);
    const manufacturerStatus = normaliseList(params.manufacturerStatus);
    const priority = normaliseList(params.priority);
    return useQuery<FetchGroupsResponse>({
        queryKey: [
            'work-queue',
            'groups',
            practice,
            assignee,
            team,
            tags,
            monitoring,
            manufacturerStatus,
            priority
        ],
        queryFn: () =>
            fetchGroups({
                practice: practice ?? undefined,
                assignee: assignee ?? undefined,
                team: team ?? undefined,
                tags: tags ?? undefined,
                monitoring: monitoring ?? undefined,
                manufacturerStatus: manufacturerStatus ?? undefined,
                priority: priority ?? undefined
            }),
        staleTime: ONE_MINUTE,
        refetchInterval: AUTO_REFRESH_MS,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always'
    });
}

interface UseCasesParams {
    key: string | null;
    practice?: string[];
    assignee?: string[];
    team?: string[];
    tags?: string[];
    monitoring?: string[];
    manufacturerStatus?: string[];
    priority?: string[];
    sortBy?: SortBy;
    dir?: SortDir;
    limit?: number;
    page?: number;
    splitByPriority?: boolean;
    // Caller override so the parent can suppress this query while a different
    // strategy (e.g. useInfiniteCases for scroll mode) is in effect.
    enabled?: boolean;
}

export function useCases(params: UseCasesParams) {
    const { key, sortBy, dir, limit, page, splitByPriority, enabled = true } = params;
    const practice = normaliseList(params.practice);
    const assignee = normaliseList(params.assignee);
    const team = normaliseList(params.team);
    const tags = normaliseList(params.tags);
    const monitoring = normaliseList(params.monitoring);
    const manufacturerStatus = normaliseList(params.manufacturerStatus);
    const priority = normaliseList(params.priority);
    // `page` is the last element of the key; everything before it is the content
    // identity (step + filters + sort). splitByPriority changes result ordering
    // so it must be part of the key.
    const queryKey = [
        'work-queue',
        'cases',
        key,
        practice,
        assignee,
        team,
        tags,
        monitoring,
        manufacturerStatus,
        priority,
        sortBy,
        dir,
        limit,
        splitByPriority ?? false,
        page
    ];
    return useQuery<FetchCasesResponse>({
        queryKey,
        queryFn: () =>
            fetchCases({
                key,
                practice: practice ?? undefined,
                assignee: assignee ?? undefined,
                team: team ?? undefined,
                tags: tags ?? undefined,
                monitoring: monitoring ?? undefined,
                manufacturerStatus: manufacturerStatus ?? undefined,
                priority: priority ?? undefined,
                sortBy,
                dir,
                limit,
                page,
                splitByPriority
            }),
        staleTime: ONE_MINUTE,
        refetchInterval: AUTO_REFRESH_MS,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        // Keep the previous rows visible ONLY across pure pagination — identical
        // step/search/filters/sort, differing only by the trailing `page`. Any
        // content change (step, search, filter, sort) drops the placeholder so we
        // never show stale rows under a new query — e.g. a no-match search rendering
        // the prior step's patients beneath its "No matches" state.
        placeholderData: (previousData, previousQuery) => {
            const prev = previousQuery?.queryKey;
            if (!prev) return undefined;
            const samePagelessIdentity =
                JSON.stringify(prev.slice(0, -1)) === JSON.stringify(queryKey.slice(0, -1));
            return samePagelessIdentity ? previousData : undefined;
        },
        enabled: enabled && key !== null
    });
}

// Infinite-scroll variant of useCases. Used by the Work Queue's "scroll" view
// mode: small chunks fetched on the fly as the user scrolls, instead of one
// big 500-row fetch. The query key is intentionally distinct from useCases so
// the two modes don't share cache (their pagination semantics differ).
// Single-case fetch fallback for deep-links. Resolves a case that isn't in the
// currently-loaded list (different group/filter/page) so the detail panel can
// open and the queue can jump to its group. Disabled unless an id is present
// and the caller opts in (i.e. the case is genuinely missing from the list).
export function useCase(patientId: string | null, enabled = true) {
    return useQuery<WorkQueueCase>({
        queryKey: ['work-queue', 'case', patientId],
        queryFn: () => fetchCase(patientId as string),
        enabled: enabled && !!patientId,
        staleTime: ONE_MINUTE
    });
}

// Distinct manufacturer-reported case statuses for the filter dropdown.
// Longer stale time — the list only changes when the portal sync writes a
// value it hasn't written before.
const FILTER_OPTIONS_STALE_MS = 5 * ONE_MINUTE;

export function useManufacturerStatusOptions() {
    return useQuery<string[]>({
        queryKey: ['work-queue', 'manufacturer-statuses'],
        queryFn: fetchManufacturerStatuses,
        staleTime: FILTER_OPTIONS_STALE_MS
    });
}

interface UseInfiniteCasesParams {
    key: string | null;
    practice?: string[];
    assignee?: string[];
    team?: string[];
    tags?: string[];
    monitoring?: string[];
    manufacturerStatus?: string[];
    priority?: string[];
    sortBy?: SortBy;
    dir?: SortDir;
    limit?: number;
    splitByPriority?: boolean;
    enabled?: boolean;
}

export function useInfiniteCases(params: UseInfiniteCasesParams) {
    const { key, sortBy, dir, limit, splitByPriority, enabled = true } = params;
    const practice = normaliseList(params.practice);
    const assignee = normaliseList(params.assignee);
    const team = normaliseList(params.team);
    const tags = normaliseList(params.tags);
    const monitoring = normaliseList(params.monitoring);
    const manufacturerStatus = normaliseList(params.manufacturerStatus);
    const priority = normaliseList(params.priority);
    return useInfiniteQuery<FetchCasesResponse>({
        queryKey: [
            'work-queue',
            'cases-infinite',
            key,
            practice,
            assignee,
            team,
            tags,
            monitoring,
            manufacturerStatus,
            priority,
            sortBy,
            dir,
            limit,
            splitByPriority ?? false
        ],
        initialPageParam: 1,
        queryFn: ({ pageParam }) =>
            fetchCases({
                key,
                practice: practice ?? undefined,
                assignee: assignee ?? undefined,
                team: team ?? undefined,
                tags: tags ?? undefined,
                monitoring: monitoring ?? undefined,
                manufacturerStatus: manufacturerStatus ?? undefined,
                priority: priority ?? undefined,
                sortBy,
                dir,
                limit,
                page: pageParam as number,
                splitByPriority
            }),
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        staleTime: ONE_MINUTE,
        refetchInterval: AUTO_REFRESH_MS,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        // No placeholderData: pages accumulate internally via fetchNextPage, so any
        // query-key change here is a content change (step / search / filter / sort).
        // Carrying the prior query's rows across it caused the bug where searching
        // within a step showed that step's old patients (duplicated across the
        // accumulated pages) beneath the new result's "No matches" state. React
        // Query still serves cached rows instantly when returning to a key it has.
        enabled: enabled && key !== null
    });
}

const SEARCH_STALE_TIME = 30 * 1000;
const SEARCH_MIN_TERM_LENGTH = 2;
const SEARCH_PAGE_SIZE = 10;

export interface CaseSearchResult {
    results: WorkQueueCase[];
    total: number;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    isFetching: boolean;
    /** True while `results` still shows the PREVIOUS term's rows (placeholder)
     *  and the current term's first page is in flight — don't select from it. */
    isPlaceholderData: boolean;
}

export function useCaseSearch(term: string): CaseSearchResult {
    const trimmed = term.trim();
    const enabled = trimmed.length >= SEARCH_MIN_TERM_LENGTH;

    const query = useInfiniteQuery<SearchCasesResponse>({
        queryKey: ['work-queue', 'search', trimmed],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => searchCases(trimmed, SEARCH_PAGE_SIZE, pageParam as number),
        getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
        enabled,
        staleTime: SEARCH_STALE_TIME,
        // Keep prior pages visible while a new term is still being typed — prevents
        // the dropdown from flashing empty between debounce cycles.
        placeholderData: (previousData) => previousData
    });

    const results = query.data?.pages.flatMap((p) => p.results) ?? [];

    return {
        results,
        total: query.data?.pages[0]?.total ?? 0,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isFetching: query.isFetching,
        isPlaceholderData: query.isPlaceholderData
    };
}

interface AssignParams {
    patientId: string;
    email: string | null; // null = unassign
    expectedAssignedTo: string | null;
    // Targets a specific team's assignee slot; omitted keeps today's behaviour
    // of targeting the case's overall assignee.
    team?: string;
}

// Surgically patches the assignee on every cached case row (paged, infinite,
// and single-case caches). A blanket invalidation refetches every loaded page
// — and each page re-signs profile-picture URLs server-side — just to change
// one row's assignee, which made assignment feel slow in production.
function patchTeamAssignees(
    teamAssignees: Record<string, string> | undefined,
    team: string,
    assignee: string | null
): Record<string, string> {
    const next = { ...teamAssignees };
    if (assignee === null) delete next[team];
    else next[team] = assignee;
    return next;
}

function patchCachedAssignee(
    queryClient: QueryClient,
    patientId: string,
    assignee: string | null,
    team?: string
): void {
    // A team-targeted write ALSO moves current_assigned_to server-side whenever
    // it targets the case's current slot (see work-queue.assignment.ts's
    // targetsCurrentSlot), so the flat `assignee` has to be patched alongside
    // the map - otherwise the list's assignee column keeps showing the previous
    // owner until a refetch. Hold steps are the known gap: the server resolves
    // the slot hold-aware via slotTeamForStatus, which can differ from c.team.
    const patchRow = (c: WorkQueueCase): WorkQueueCase => {
        if (c.id !== patientId) return c;
        if (!team) return { ...c, assignee };
        const patched = {
            ...c,
            teamAssignees: patchTeamAssignees(c.teamAssignees, team, assignee)
        };
        return team === c.team ? { ...patched, assignee } : patched;
    };
    const patchPage = (page: FetchCasesResponse): FetchCasesResponse => ({
        ...page,
        cases: page.cases.map(patchRow)
    });

    queryClient.setQueriesData<FetchCasesResponse>({ queryKey: ['work-queue', 'cases'] }, (data) =>
        data ? patchPage(data) : data
    );
    queryClient.setQueriesData<{ pages: FetchCasesResponse[]; pageParams: unknown[] }>(
        { queryKey: ['work-queue', 'cases-infinite'] },
        (data) => (data ? { ...data, pages: data.pages.map(patchPage) } : data)
    );
    queryClient.setQueryData<WorkQueueCase>(['work-queue', 'case', patientId], (c) =>
        c ? patchRow(c) : c
    );
}

// Mutation that handles both assign + unassign. On success the cached rows are
// patched in place; a full groups+cases refetch only happens when an assignee
// filter is active (the only case where the visible row set itself changes).
export function useAssignCase() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ patientId, email, expectedAssignedTo, team }: AssignParams) => {
            if (email === null) {
                return unassignWorkQueueCase(patientId, expectedAssignedTo, team);
            }
            return assignWorkQueueCase(patientId, email, expectedAssignedTo, team);
        },
        onSuccess: (_data, { patientId, email, team }) => {
            patchCachedAssignee(queryClient, patientId, email, team);
            void invalidateSidebarCounts(queryClient);
            if (new URLSearchParams(globalThis.location.search).has(ASSIGNEE_URL_PARAM)) {
                invalidateWorkQueue(queryClient);
            }
        }
    });
}
